/**
 * Tax Summary / Txns holdings — display only.
 *
 * Equity Shares (Owned) does not add commission_balances.total_earned_shares
 * (those L1 credits are already materialised as purchase rows, then often
 * consolidated). Consolidation also zeroes commission_conversions.shares_requested
 * and writes a Consolidated USDT row. The client used to keep painting that
 * zeroed conversion as a Package line (paid + reconstructed bonus), so Tax
 * Summary Total Shares Purchased drifted above owned.
 *
 * This helper builds the same Txns list the tax PDF sums, without those two
 * extras. It never writes shares, commissions, or supply.
 */

import { displayPackageLabel, effectiveRowShares, type ShareRowLike } from './shareRowDisplay'

export type PortfolioHolding = {
  id: string
  shares_purchased: number
  bonus_shares?: number
  total_amount: number
  price_per_share: number
  phase_name: string
  phase_number: number
  purchase_date: string
  status: string
  transaction_reference?: string
  transaction_type: 'purchase' | 'conversion' | 'commission' | 'transfer_sent' | 'transfer_received'
}

export const isLiveCommissionConversion = (conversion: { shares_requested?: unknown } | null | undefined): boolean => {
  const paid = Number(conversion?.shares_requested)
  return Number.isFinite(paid) && paid > 0
}

export const isTaxEquityHolding = (holding: { transaction_type?: string } | null | undefined): boolean =>
  holding?.transaction_type !== 'commission'

export const taxSummaryShareTotal = (holdings: ShareRowLike[] | null | undefined): number =>
  (holdings || []).filter(isTaxEquityHolding).reduce((sum, row) => sum + effectiveRowShares(row), 0)

const parseConversionSnapshot = (conversion: any): { bonus: number; label: string } => {
  try {
    const snap = typeof conversion?.admin_notes === 'string' ? JSON.parse(conversion.admin_notes) : conversion?.admin_notes
    if (snap?.kind === 'commission_conversion' && Array.isArray(snap.packages) && snap.packages.length > 0) {
      const bonus = snap.packages.reduce((sum: number, line: any) => sum + (Number(line.investorBonus) || 0), 0)
      const label = snap.packages.map((line: any) => `${line.name || line.packageId} × ${line.qty}`).join(', ')
      return { bonus, label }
    }
  } catch {
    /* snapshot is display-only; a bad note must not invent shares */
  }
  return { bonus: 0, label: 'Commission Conversion' }
}

export function buildPortfolioHoldings(data: {
  purchases?: any[]
  commissionShares?: any[]
  transfersIn?: any[]
  transfersOut?: any[]
}): PortfolioHolding[] {
  const purchases = data?.purchases || []
  const commissionShares = data?.commissionShares || []
  const transfersIn = data?.transfersIn || []
  const transfersOut = data?.transfersOut || []

  const foldedConversionIds = new Set<string>()
  const conversionHoldings: PortfolioHolding[] = []

  for (const conversion of commissionShares) {
    if (!isLiveCommissionConversion(conversion)) continue
    const { bonus, label } = parseConversionSnapshot(conversion)
    if (bonus > 0 && conversion.id) foldedConversionIds.add(String(conversion.id))
    const paid = Number(conversion.shares_requested) || 0
    conversionHoldings.push({
      id: `conversion-${conversion.id}`,
      shares_purchased: paid + bonus,
      bonus_shares: bonus,
      total_amount: parseFloat(conversion.usdt_amount || 0),
      price_per_share: paid > 0 ? parseFloat(conversion.usdt_amount) / paid : 0,
      phase_name: bonus > 0 ? `${label} — ${paid} paid + ${bonus} bonus` : label,
      phase_number: 0,
      purchase_date: conversion.approved_at || conversion.created_at,
      status: conversion.status,
      transaction_reference: 'USDT→Shares',
      transaction_type: 'conversion',
    })
  }

  const allHoldings: PortfolioHolding[] = []

  for (const purchase of purchases) {
    if (purchase.payment_method === 'Commission Conversion') continue
    const reference = String(purchase.transaction_reference || '')
    const foldedGift = purchase.payment_method === 'PCAP_2026_BUYER_GIFT'
      && Array.from(foldedConversionIds).some((id) => reference.includes('CONV:' + id))
    if (foldedGift) continue
    allHoldings.push({
      id: `purchase-${purchase.id}`,
      shares_purchased: purchase.shares_purchased || 0,
      bonus_shares: purchase.bonus_shares || 0,
      total_amount: purchase.total_amount || 0,
      price_per_share: purchase.shares_purchased > 0 ? purchase.total_amount / purchase.shares_purchased : 0,
      phase_name: displayPackageLabel(purchase.package_name) || purchase.phase?.phase_name || 'Share Purchase',
      phase_number: 0,
      purchase_date: purchase.created_at,
      status: purchase.status,
      transaction_reference: purchase.payment_method || 'N/A',
      transaction_type: 'purchase',
    })
  }

  allHoldings.push(...conversionHoldings)

  for (const transfer of transfersOut) {
    allHoldings.push({
      id: `transfer-sent-${transfer.id}`,
      shares_purchased: -parseFloat(transfer.shares_transferred || transfer.shares_amount),
      total_amount: 0,
      price_per_share: 0,
      phase_name: 'Share Transfer (Sent)',
      phase_number: 0,
      purchase_date: transfer.completed_at || transfer.created_at,
      status: transfer.status,
      transaction_reference: `To User ${transfer.to_user_id}`,
      transaction_type: 'transfer_sent',
    })
  }

  for (const transfer of transfersIn) {
    allHoldings.push({
      id: `transfer-received-${transfer.id}`,
      shares_purchased: parseFloat(transfer.shares_transferred || transfer.shares_amount),
      total_amount: 0,
      price_per_share: 0,
      phase_name: 'Share Transfer (Received)',
      phase_number: 0,
      purchase_date: transfer.completed_at || transfer.created_at,
      status: transfer.status,
      transaction_reference: `From User ${transfer.from_user_id}`,
      transaction_type: 'transfer_received',
    })
  }

  allHoldings.sort((a, b) => new Date(b.purchase_date).getTime() - new Date(a.purchase_date).getTime())
  return allHoldings
}
